import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, NavLink, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import { useTheme } from "../../contexts/ThemeContext";
import api from "../../services/api";
import { getStoredUser, setStoredUser, removeStoredUser } from "../../utils/authStorage";
import "./AdminLayout.css";

const AdminLayout = () => {
  const navigate = useNavigate();
  const { logoUrl, loading } = useTheme();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openMenus, setOpenMenus] = useState([]); // Track which menus are open

  useEffect(() => {
    // Check if user is logged in
    const userData = getStoredUser();
    if (!userData) {
      navigate("/admin/login");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  // Perbaiki unik_id (id_pendamping) untuk R.45/R.47 — sesi lama bisa masih pakai id PTSP
  useEffect(() => {
    const syncPendampingSession = async () => {
      if (!user?.userid) return;
      const role = (user.role || "").trim();
      if (role !== "R.45" && role !== "R.47") return;

      try {
        const { data } = await api.get("/pendamping/profil-saya", {
          params: { userid: user.userid },
        });
        if (!data?.success || !data.id_pendamping) return;

        if (user.unik_id === data.id_pendamping && user.id_pendamping === data.id_pendamping) {
          return;
        }

        const updatedUser = {
          ...user,
          unik_id: data.id_pendamping,
          id_pendamping: data.id_pendamping,
        };
        setStoredUser(updatedUser);
        setUser(updatedUser);
      } catch (err) {
        console.warn("[AdminLayout] Gagal sync id_pendamping:", err.message);
      }
    };

    syncPendampingSession();
  }, [user?.userid, user?.role]);

  const handleLogout = () => {
    removeStoredUser();
    navigate("/admin/login");
  };

  const location = useLocation();

  // Auto-close sidebar on mobile when navigating
  useEffect(() => {
    if (window.innerWidth <= 992) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleMenu = (menuKey) => {
    // If sidebar is closed, open it first when clicking a menu group
    if (!sidebarOpen) {
      setSidebarOpen(true);
      // Also expand the menu group
      if (!openMenus.includes(menuKey)) {
        setOpenMenus((prev) => [...prev, menuKey]);
      }
      return;
    }

    // Normal toggle behavior when sidebar is open
    setOpenMenus((prev) =>
      prev.includes(menuKey)
        ? prev.filter((key) => key !== menuKey)
        : [...prev, menuKey],
    );
  };

  if (!user) {
    return null;
  }

  // Check if user has admin role (R.1)
  const userRole = (user.role || "").trim();
  const hasAdminRole = ["R.1", "R.2"].includes(userRole);
  const hasPelatihanRole = ["R.1", "R.40"].includes(userRole);
  const hasRegulerRole = ["R.2", "R.5"].includes(userRole);

  // Debug logging
  console.log("[AdminLayout] User role:", userRole);
  console.log("[AdminLayout] hasRegulerRole:", hasRegulerRole);
  console.log("[AdminLayout] Full user object:", user);

  return (
    <div className="admin-wrapper">
      {/* Mobile Overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? "active" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? "open" : "closed"}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo Lembaga"
                className="admin-logo-img"
              />
            ) : loading ? (
              <span className="admin-logo-placeholder" aria-hidden="true" />
            ) : null}
          </div>
          <h3 className="sidebar-title">Halal Center</h3>
        </div>

        <nav className="sidebar-nav">
          {/* Dashboard - Always visible */}
          <NavLink to="/admin/dashboard" className="nav-item" title="Dashboard">
            <i className="fas fa-home"></i>
            <span>Dashboard</span>
          </NavLink>

          {/* Profil - Calon Peserta (R.47) & Pendamping (R.45) */}
          {(user.role === "R.47" || user.role === "R.45") && (
            <NavLink
              to="/admin/profil"
              className="nav-item"
              title="Profil Saya"
            >
              <i className="fas fa-user-circle"></i>
              <span>Profil Saya</span>
            </NavLink>
          )}

          {/* Profil - Only for role R.48 (Penyelia Halal) */}
          {user.role === "R.48" && (
            <NavLink
              to="/admin/profil-penyelia"
              className="nav-item"
              title="Profil Saya"
            >
              <i className="fas fa-user-circle"></i>
              <span>Profil Saya</span>
            </NavLink>
          )}

          {/* Pengajuan Sertifikat Halal - Only for role R.45 */}
          {user.role === "R.45" && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("sertifikat-halal") ? "active" : ""}`}
                onClick={() => toggleMenu("sertifikat-halal")}
                title="Pengajuan Sertifikat Halal"
              >
                <div className="menu-header-content">
                  <i className="fas fa-certificate"></i>
                  <span>Pengajuan Sertifikat Halal</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("sertifikat-halal") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("sertifikat-halal") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/sertifikat/daftar-pengajuan-pu"
                  className="submenu-item"
                  title="Daftar Pengajuan PU"
                >
                  <i className="fas fa-list-alt"></i>
                  <span>Daftar Pengajuan PU</span>
                </NavLink>

                <NavLink
                  to="/admin/sertifikat/proses-verval"
                  className="submenu-item"
                  title="Proses Verval"
                >
                  <i className="fas fa-tasks"></i>
                  <span>Proses Verval</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Laporan - Only for role R.45 */}
          {user.role === "R.45" && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("laporan-r45") ? "active" : ""}`}
                onClick={() => toggleMenu("laporan-r45")}
                title="Laporan"
              >
                <div className="menu-header-content">
                  <i className="fas fa-chart-bar"></i>
                  <span>Laporan</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("laporan-r45") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("laporan-r45") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/laporan/hasil-verval"
                  className="submenu-item"
                  title="Laporan Hasil Verval"
                >
                  <i className="fas fa-file-alt"></i>
                  <span>Laporan Hasil Verval</span>
                </NavLink>

                <NavLink
                  to="/admin/laporan/sh-terbit"
                  className="submenu-item"
                  title="Laporan SH Terbit"
                >
                  <i className="fas fa-award"></i>
                  <span>Laporan SH Terbit</span>
                </NavLink>

                <NavLink
                  to="/admin/laporan/cek-status-pembayaran"
                  className="submenu-item"
                  title="Cek Status Pembayaran"
                >
                  <i className="fas fa-search-dollar"></i>
                  <span>Cek Status Pembayaran</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Admin Menu Group - Only for role R.1, R.2, R.5 */}
          {hasAdminRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("admin") ? "active" : ""}`}
                onClick={() => toggleMenu("admin")}
                title="Admin"
              >
                <div className="menu-header-content">
                  <i className="fas fa-cog"></i>
                  <span>Admin</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("admin") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("admin") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/content"
                  className="submenu-item"
                  title="Kelola Content"
                >
                  <i className="fas fa-file-alt"></i>
                  <span>Kelola Content</span>
                </NavLink>

                <NavLink
                  to="/admin/slider"
                  className="submenu-item"
                  title="Kelola Slider"
                >
                  <i className="fas fa-images"></i>
                  <span>Kelola Slider</span>
                </NavLink>

                <NavLink
                  to="/admin/berita"
                  className="submenu-item"
                  title="Kelola Berita"
                >
                  <i className="fas fa-newspaper"></i>
                  <span>Kelola Berita</span>
                </NavLink>

                <NavLink
                  to="/admin/layanan"
                  className="submenu-item"
                  title="Edit Layanan"
                >
                  <i className="fas fa-cog"></i>
                  <span>Edit Layanan</span>
                </NavLink>

                <NavLink
                  to="/admin/welcome"
                  className="submenu-item"
                  title="Edit Welcome Message"
                >
                  <i className="fas fa-home"></i>
                  <span>Welcome Message</span>
                </NavLink>

                <NavLink
                  to="/admin/client"
                  className="submenu-item"
                  title="Kelola Client"
                >
                  <i className="fas fa-handshake"></i>
                  <span>Kelola Client</span>
                </NavLink>

                <NavLink
                  to="/admin/settings"
                  className="submenu-item"
                  title="Pengaturan Lembaga"
                >
                  <i className="fas fa-cogs"></i>
                  <span>Pengaturan Lembaga</span>
                </NavLink>

                <NavLink
                  to="/admin/contact"
                  className="submenu-item"
                  title="Pesan Kontak"
                >
                  <i className="fas fa-envelope"></i>
                  <span>Pesan Kontak</span>
                </NavLink>

                <NavLink
                  to="/admin/menu"
                  className="submenu-item"
                  title="Kelola Menu"
                >
                  <i className="fas fa-bars"></i>
                  <span>Kelola Menu</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Pelatihan Pendamping Menu Group */}
          {hasPelatihanRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("pelatihan-pendamping") ? "active" : ""}`}
                onClick={() => toggleMenu("pelatihan-pendamping")}
                title="Pelatihan Pendamping"
              >
                <div className="menu-header-content">
                  <i className="fas fa-chalkboard-teacher"></i>
                  <span>Pelatihan Pendamping</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("pelatihan-pendamping") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("pelatihan-pendamping") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/pelatihan/jadwal"
                  className="submenu-item"
                  title="Entri Jadwal Pelatihan"
                >
                  <i className="fas fa-calendar-plus"></i>
                  <span>Entri Jadwal Pelatihan</span>
                </NavLink>

                {hasAdminRole && (
                  <NavLink
                    to="/admin/pelatihan/calon-pendamping"
                    className="submenu-item"
                    title="Daftar Calon Peserta"
                  >
                    <i className="fas fa-user-graduate"></i>
                    <span>Daftar Calon Peserta</span>
                  </NavLink>
                )}

                {hasAdminRole && (
                  <NavLink
                    to="/admin/pelatihan/peserta"
                    className="submenu-item"
                    title="Daftar Peserta Pelatihan"
                  >
                    <i className="fas fa-users"></i>
                    <span>Daftar Peserta Pelatihan</span>
                  </NavLink>
                )}
              </div>
            </div>
          )}

          {/* Pendamping Menu Group - Only for role R.1 and R.40 */}
          {hasPelatihanRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("pendamping") ? "active" : ""}`}
                onClick={() => toggleMenu("pendamping")}
                title="Pendamping"
              >
                <div className="menu-header-content">
                  <i className="fas fa-user-tie"></i>
                  <span>Pendamping</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("pendamping") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("pendamping") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/pendamping/daftar"
                  className="submenu-item"
                  title="Daftar Pendamping"
                >
                  <i className="fas fa-list"></i>
                  <span>Daftar Pendamping</span>
                </NavLink>

                <NavLink
                  to="/admin/pendamping/laporan-kinerja"
                  className="submenu-item"
                  title="Laporan Kinerja Pendamping"
                >
                  <i className="fas fa-chart-line"></i>
                  <span>Laporan Kinerja Pendamping</span>
                </NavLink>

                {userRole === "R.1" && (
                  <NavLink
                    to="/admin/pendamping/laporan-referral"
                    className="submenu-item"
                    title="Laporan Referral"
                  >
                    <i className="fas fa-handshake"></i>
                    <span>Laporan Referral</span>
                  </NavLink>
                )}
              </div>
            </div>
          )}

          {/* Pengajuan SH Menu Group - selevel dengan Pendamping, di bawahnya */}
          {hasPelatihanRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("pengajuan-sh") ? "active" : ""}`}
                onClick={() => toggleMenu("pengajuan-sh")}
                title="Pengajuan SH"
              >
                <div className="menu-header-content">
                  <i className="fas fa-file-signature"></i>
                  <span>Pengajuan SH</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("pengajuan-sh") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("pengajuan-sh") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/pengajuan-sh/daftar-pengajuan"
                  className="submenu-item"
                  title="Daftar Pengajuan"
                >
                  <i className="fas fa-list"></i>
                  <span>Daftar Pengajuan</span>
                </NavLink>

                <NavLink
                  to="/admin/pengajuan-sh/cek-pengajuan"
                  className="submenu-item"
                  title="Cek Pengajuan"
                >
                  <i className="fas fa-search"></i>
                  <span>Cek Pengajuan</span>
                </NavLink>

                <NavLink
                  to="/admin/pengajuan-sh/cek-pembayaran"
                  className="submenu-item"
                  title="Cek Pembayaran"
                >
                  <i className="fas fa-credit-card"></i>
                  <span>Cek Pembayaran</span>
                </NavLink>
                <NavLink
                  to="/admin/pengajuan-sh/rekap-by-pendamping"
                  className="submenu-item"
                  title="Rekap By Pendamping"
                >
                  <i className="fas fa-users"></i>
                  <span>Rekap By Pendamping</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Informasi Tagihan Menu Group - Only for role R.1 and R.40 */}
          {hasPelatihanRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("informasi-tagihan") ? "active" : ""}`}
                onClick={() => toggleMenu("informasi-tagihan")}
                title="Informasi Tagihan"
              >
                <div className="menu-header-content">
                  <i className="fas fa-file-invoice-dollar"></i>
                  <span>Informasi Tagihan</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("informasi-tagihan") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("informasi-tagihan") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/tagihan/daftar"
                  className="submenu-item"
                  title="Daftar Tagihan"
                >
                  <i className="fas fa-receipt"></i>
                  <span>Daftar Tagihan</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Pengajuan Reguler Menu Group - Only for role R.2 and R.5 */}
          {hasRegulerRole && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("pengajuan-reguler") ? "active" : ""}`}
                onClick={() => toggleMenu("pengajuan-reguler")}
                title="Pengajuan Reguler"
              >
                <div className="menu-header-content">
                  <i className="fas fa-file-alt"></i>
                  <span>Pengajuan Reguler</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("pengajuan-reguler") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("pengajuan-reguler") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/reguler/daftar-pengajuan"
                  className="submenu-item"
                  title="Daftar Pengajuan Reguler"
                >
                  <i className="fas fa-list"></i>
                  <span>Daftar Pengajuan Reguler</span>
                </NavLink>

                <NavLink
                  to="/admin/reguler/rekapitulasi"
                  className="submenu-item"
                  title="Rekapitulasi"
                >
                  <i className="fas fa-chart-bar"></i>
                  <span>Rekapitulasi</span>
                </NavLink>

                <NavLink
                  to="/admin/reguler/daftar-invoice"
                  className="submenu-item"
                  title="Daftar Invoice"
                >
                  <i className="fas fa-file-invoice"></i>
                  <span>Daftar Invoice</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Jadwal Pelatihan - Only for role R.41 */}
          {user.role === "R.41" && (
            <NavLink
              to="/admin/pelatihan/jadwal-r41"
              className="nav-item"
              title="Jadwal Pelatihan"
            >
              <i className="fas fa-calendar-alt"></i>
              <span>Jadwal Pelatihan</span>
            </NavLink>
          )}

          {/* Peserta Pelatihan Menu Group - Only for role R.41 */}
          {user.role === "R.41" && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("peserta-pelatihan") ? "active" : ""}`}
                onClick={() => toggleMenu("peserta-pelatihan")}
                title="Peserta Pelatihan"
              >
                <div className="menu-header-content">
                  <i className="fas fa-user-graduate"></i>
                  <span>Peserta Pelatihan</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("peserta-pelatihan") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("peserta-pelatihan") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/peserta/entri-calon"
                  className="submenu-item"
                  title="Entri Calon Peserta"
                >
                  <i className="fas fa-user-plus"></i>
                  <span>Entri Calon Peserta</span>
                </NavLink>

                <NavLink
                  to="/admin/peserta/daftar-calon"
                  className="submenu-item"
                  title="Daftar Calon Peserta"
                >
                  <i className="fas fa-list"></i>
                  <span>Daftar Calon Peserta</span>
                </NavLink>

                <NavLink
                  to="/admin/peserta/daftar-peserta"
                  className="submenu-item"
                  title="Daftar Peserta"
                >
                  <i className="fas fa-users"></i>
                  <span>Daftar Peserta</span>
                </NavLink>

                <NavLink
                  to="/admin/peserta/penerbitan-sertifikat"
                  className="submenu-item"
                  title="Penerbitan Sertifikat"
                >
                  <i className="fas fa-certificate"></i>
                  <span>Penerbitan Sertifikat</span>
                </NavLink>

                <NavLink
                  to="/admin/peserta/daftar-sertifikat-terbit"
                  className="submenu-item"
                  title="Daftar Sertifikat Terbit"
                >
                  <i className="fas fa-award"></i>
                  <span>Daftar Sertifikat Terbit</span>
                </NavLink>

                <NavLink
                  to="/admin/peserta/inquiry"
                  className="submenu-item"
                  title="Inquiry Peserta Pelatihan"
                >
                  <i className="fas fa-search"></i>
                  <span>Inquiry Peserta Pelatihan</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* LPK Menu Group - Only for role R.41 */}
          {user.role === "R.41" && (
            <div className="menu-group">
              <div
                className={`menu-header ${openMenus.includes("lpk") ? "active" : ""}`}
                onClick={() => toggleMenu("lpk")}
                title="Proses TTE Pelatihan"
              >
                <div className="menu-header-content">
                  <i className="fas fa-school"></i>
                  <span>Proses TTE Pelatihan</span>
                </div>
                <i
                  className={`fas fa-chevron-${openMenus.includes("lpk") ? "down" : "right"} menu-arrow`}
                ></i>
              </div>
              <div
                className={`submenu ${openMenus.includes("lpk") ? "open" : ""}`}
              >
                <NavLink
                  to="/admin/lpk/data-pelatihan-penyelia-halal"
                  className="submenu-item"
                  title="Data Pelatihan Penyelia Halal"
                >
                  <i className="fas fa-user-check"></i>
                  <span>Data Pelatihan Penyelia Halal</span>
                </NavLink>

                <NavLink
                  to="/admin/lpk/data-pelatihan-auditor-halal"
                  className="submenu-item"
                  title="Data Pelatihan Auditor Halal"
                >
                  <i className="fas fa-user-shield"></i>
                  <span>Data Pelatihan Auditor Halal</span>
                </NavLink>

                <NavLink
                  to="/admin/lpk/data-pelatihan-jph-lainnya"
                  className="submenu-item"
                  title="Data Pelatihan JPH Lainnya"
                >
                  <i className="fas fa-users"></i>
                  <span>Data Pelatihan JPH Lainnya</span>
                </NavLink>

                <NavLink
                  to="/admin/lpk/inquiry"
                  className="submenu-item"
                  title="Inquiry"
                >
                  <i className="fas fa-search"></i>
                  <span>Inquiry</span>
                </NavLink>

                <NavLink
                  to="/admin/lpk/invoice"
                  className="submenu-item"
                  title="Invoice"
                >
                  <i className="fas fa-file-invoice"></i>
                  <span>Invoice</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Ganti Password - Available to all users */}
          <NavLink
            to="/admin/ganti-password"
            className="nav-item"
            title="Ganti Password"
          >
            <i className="fas fa-lock"></i>
            <span>Ganti Password</span>
          </NavLink>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            <div className="user-details">
              <div className="user-name">{user.username}</div>
              <div className="user-role">
                {{
                  "R.1": "ADMIN",
                  "R.5": "REGULER",
                  "R.40": "PELATIHAN PENDAMPING",
                  "R.41": "PELATIHAN HALAL",
                  "R.45": "PENDAMPING",
                  "R.47": "PESERTA PELATIHAN",
                }[user.role] ||
                  user.role ||
                  "-"}
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`admin-main ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
      >
        {/* Top Bar */}
        <header className="admin-topbar">
          <button className="btn-toggle-sidebar" onClick={toggleSidebar}>
            <i className="fas fa-bars"></i>
          </button>

          <div className="topbar-right">
            <button className="btn-home" onClick={() => navigate("/")}>
              <i className="fas fa-home"></i>
              <span>Lihat Website</span>
            </button>
            <button className="btn-logout" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content">
          <Container fluid>
            <Outlet />
          </Container>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
